

























































import { AxiosError } from 'axios';
import { cloneDeep } from 'lodash';
import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
  PropType
} from '@vue/composition-api';
import { useVModel } from '@vueuse/core';
import {
  createNamespacedHelpers,
  useNamespacedActions,
  useNamespacedGetters,
  useStore
} from 'vuex-composition-helpers';

import {
  RequisitionLine,
  IRequisitionLineData,
  ISystemError,
  IRequisitionLine,
  IRequisitionStatus,
  RequisitionStatusEnum
} from '@/models';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';

import { requiredValidator } from '@/utils/validators';
import { ALForm } from '@/common/component.types';

import { useUpdatePurchaseRequisitionLine } from '@/composable/purchase-requisition';
import { IRootState } from '@/store';
import {
  IRequisitionState,
  IRequisitionGetters,
  IRequisitionActions,
  IRequisitionMutations
} from '@/store/requisition';
import { IGLAccountActions, IGLAccountGetters } from '@/store/accounting';
import { IDepartmentActions, IDepartmentGetters } from '@/store/department';

import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import RequisitionDetailFormLineCard from '@/components/requisition/RequisitionDetailFormLineCard.vue';
import RequisitionLineFormFields from '@/components/requisition-common/RequisitionLineFormFields.vue';

export default defineComponent({
  name: 'RequisitionLineFormDialog',

  components: {
    ALDialog,
    ErrorAlert,
    RequisitionDetailFormLineCard,
    RequisitionLineFormFields
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    requisitionId: {
      type: Number,
      required: true
    },
    requisitionLine: {
      type: Object as PropType<IRequisitionLine | null>,
      default: null
    },

    requisitionStatus: {
      type: Object as PropType<IRequisitionStatus>,
      required: true
    }
  },

  emits: ['input'],
  setup(props, { emit }) {
    const localDialog = useVModel(props, 'value', emit, {
      passive: true,
      eventName: 'input'
    });

    const requisitionLineData = ref<IRequisitionLineData>();

    const isFormValid = ref(false);

    const lineItemLoading = ref(false);

    const formRef = ref<ALForm | null>(null);

    const subtitle = computed(() => {
      return props.requisitionLine
        ? 'Edit Requisition Line Item'
        : 'Create New Requisition Line Item';
    });

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = requisitionLineError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    watch(
      () => props.value,
      () => {
        resetFormValidation();
      }
    );
    watch(
      () => props.requisitionLine,
      newVal => {
        if (newVal) {
          requisitionLineData.value = cloneDeep(newVal);
        }
      },
      { immediate: true }
    );

    const store = useStore<IRootState>();

    const { useActions } = createNamespacedHelpers<
      IRequisitionState,
      IRequisitionGetters,
      IRequisitionActions,
      IRequisitionMutations
    >(store, 'requisition');

    const { updateRequisitionLine } = useActions(['updateRequisitionLine']);

    const accountingActions = useNamespacedActions<IGLAccountActions>(
      store,
      'accounting',
      ['fetchGLAccounts']
    );

    const accountingGetters = useNamespacedGetters<IGLAccountGetters>(
      store,
      'accounting',
      ['getGLAccounts']
    );

    const departmentActions = useNamespacedActions<IDepartmentActions>(
      store,
      'department',
      ['fetchDepartments']
    );

    const departmentGetters = useNamespacedGetters<IDepartmentGetters>(
      store,
      'department',
      ['getDepartments']
    );

    onMounted(() => {
      lineItemLoading.value = true;

      Promise.all([
        accountingActions.fetchGLAccounts(),
        departmentActions.fetchDepartments()
      ])
        .catch(e => {
          error.value = setErrorFromAxiosError(e as AxiosError);
        })
        .finally(() => {
          lineItemLoading.value = false;
        });
    });

    const canEditAllRequisitionLine = computed(() => {
      return props.requisitionStatus.value === RequisitionStatusEnum.Draft;
    });

    function clearErrors() {
      error.value = null;
    }

    const closeDialog = () => {
      clearErrors();
      localDialog.value = false;
    };

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    const localError = ref<ISystemError | null>(null);

    const {
      isLoading,
      error: requisitionLineError,
      mutate
    } = useUpdatePurchaseRequisitionLine();

    const saveRequisitionLine = async () => {
      const valid = await validateForm();

      if (valid) {
        let requisitionLineInstance: IRequisitionLine | null = null;

        try {
          requisitionLineInstance = new RequisitionLine(
            requisitionLineData.value
          );
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (requisitionLineInstance != null) {
          let payload = requisitionLineInstance;
          payload = diffDeep(
            requisitionLineInstance,
            props.requisitionLine,
            true
          );

          mutate(
            { id: props.requisitionId, line: payload },
            {
              onSuccess: data => {
                updateRequisitionLine({
                  requisitionId: props.requisitionId,
                  line: data
                });
                emit('save');
                closeDialog();
              }
            }
          );
        }
      }
    };

    function resetFormValidation(): void {
      if (formRef.value) {
        return formRef.value.resetValidation();
      }
    }

    return {
      localDialog,
      lineItemLoading,
      isFormValid,
      formRef,
      requisitionLineData,
      isLoading,
      error,
      subtitle,
      requiredValidator,
      clearErrors,
      closeDialog,
      saveRequisitionLine,
      canEditAllRequisitionLine,

      accounts: accountingGetters.getGLAccounts,
      departments: departmentGetters.getDepartments
    };
  }
});
